import { useId } from 'react';
import { cx } from '../lib/cx';

export interface ChartPoint {
  x: string;
  y: number;
}

export interface LineChartProps {
  points: readonly ChartPoint[];
  ariaLabel: string;
  unit?: string;
  height?: number;
  className?: string;
  formatValue?: (v: number) => string;
}

/**
 * Inline SVG linijski graf. Namjerno bez chart biblioteke —
 * ARHITEKTURA.md sekcija 11: "jedna Chart komponenta s tokenima, ne pet biblioteka".
 */
export function LineChart({
  points,
  ariaLabel,
  unit,
  height = 180,
  className,
  formatValue,
}: LineChartProps) {
  const gradientId = useId();
  const W = 640;
  const H = height;
  const pad = { top: 16, right: 12, bottom: 26, left: 40 };

  if (points.length < 2) return null;

  const ys = points.map((p) => p.y);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const span = maxY - minY || 1;
  const lo = minY - span * 0.15;
  const hi = maxY + span * 0.15;

  const px = (i: number) =>
    pad.left + (i / (points.length - 1)) * (W - pad.left - pad.right);
  const py = (v: number) => pad.top + (1 - (v - lo) / (hi - lo)) * (H - pad.top - pad.bottom);

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(p.y).toFixed(1)}`).join(' ');
  const area = `${line} L${px(points.length - 1).toFixed(1)},${H - pad.bottom} L${px(0).toFixed(1)},${H - pad.bottom} Z`;
  const fmt = formatValue ?? ((v: number) => String(Math.round(v * 10) / 10));

  const ticks = [hi, (hi + lo) / 2, lo];

  return (
    <figure className={cx('m-0 w-full overflow-x-auto', className)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>

        {ticks.map((t, i) => (
          <g key={i} className="text-line">
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={py(t)}
              y2={py(t)}
              stroke="currentColor"
              strokeWidth="1"
            />
            <text
              x={pad.left - 8}
              y={py(t) + 4}
              textAnchor="end"
              className="fill-ink-muted"
              fontSize="11"
            >
              {fmt(t)}
            </text>
          </g>
        ))}

        <g className="text-accent">
          <path d={area} fill={`url(#${gradientId})`} />
          <path d={line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          {points.map((p, i) => (
            <circle key={p.x} cx={px(i)} cy={py(p.y)} r="3" fill="currentColor" />
          ))}
        </g>

        {points.map((p, i) =>
          i === 0 || i === points.length - 1 ? (
            <text
              key={`${p.x}-label`}
              x={px(i)}
              y={H - 8}
              textAnchor={i === 0 ? 'start' : 'end'}
              className="fill-ink-muted"
              fontSize="11"
            >
              {p.x}
            </text>
          ) : null,
        )}
      </svg>
      <figcaption className="sr-only">
        {ariaLabel}
        {unit ? ` (${unit})` : ''}
      </figcaption>
    </figure>
  );
}
